import React, { useState, useRef, useCallback, useEffect } from "react";

// ====================================================
// 💰 HELPER FUNCTION: CURRENCY FORMATTING
// Takes a number or string and returns the formatted currency string.
const formatCurrency = (value) => {
  if (typeof value !== "string") {
    value = String(value);
  }

  // 1. Clean the input: remove all non-digit, non-decimal characters
  let cleanedValue = value.replace(/[^\d.]/g, "");

  // Ensure we only have one decimal point
  const parts = cleanedValue.split(".");
  if (parts.length > 2) {
    cleanedValue = parts[0] + "." + parts.slice(1).join("");
  }

  // Convert to float for calculations
  let num = Number.parseFloat(cleanedValue) || 0;

  // Format to ensure standard currency structure (e.g., $X,XXX.XX)
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD", // Change this to your desired currency code
  });
  return formatter.format(num);
};

/**
 * CurrencyInput component handles controlled input state and maintains cursor position.
 * @param {object} props - Contains optional initial value or currency symbol.
 */
export const CurrencyInput = ({
  initialValue,
  value,
  currencySymbol = "$",
  placeholder = "",
  className = "",
  style = {},
  onNewValueHandler,
  ...props
}) => {
  // 1. State for the formatted display value
  const [displayValue, setDisplayValue] = useState(
    formatCurrency(initialValue),
  );

  const [lastValue, setLastValue] = useState(initialValue);

  if (lastValue !== value) {
    // don't update unnecessarily
    setLastValue(value);
    setDisplayValue(formatCurrency(initialValue));
  }

  /*useEffect(() => {
    console.log("--> FIRED: " + displayValue);
  }, [displayValue]);*/
  /*
  useEffect(() => {
    if (value == null) return;

    let cleanDisplayValue = displayValue.replace(/[^\d\.]/g, "");
    cleanDisplayValue = parseFloat(cleanDisplayValue);
    let cleanValue = `${value}`.replace(/[^\d\.]/g, "");
    cleanValue = parseFloat(cleanValue);

    if (cleanDisplayValue !== cleanValue) {
      // --- 2. STATE UPDATE (Formatting) ---
      // Generate the new formatted string based on the cleaned input
      const newDisplayValue = formatCurrency(cleanValue);

      // Update React state *first*. This triggers a re-render and updates the visible value.
      setDisplayValue(newDisplayValue);
    }
  }, [value]);*/
  // 2. Ref to access and manipulate the underlying DOM element (essential for cursor)
  const inputRef = useRef(null);

  /**
   * Custom handler to manage both state update and cursor position maintenance.
   */
  const handleChange = useCallback((event) => {
    const rawInput = event.target.value;

    // --- 1. PRE-PROCESSING & CLEANING ---
    // Get the clean, numeric value from the user's input (removing formatting characters like commas/symbols)
    let cleanedNumericValue = rawInput.replace(/[^\d\.]/g, "");

    // Handle potential decimal issues
    const parts = cleanedNumericValue.split(".");
    if (parts.length >= 2) {
      cleanedNumericValue = parts.join("");

      const reversePosition = -2; // 5 characters from the end
      const charToInsert = ".";

      // Slice from start to the reverse position, add char, then slice from reverse position to the end
      cleanedNumericValue =
        cleanedNumericValue.slice(0, reversePosition) +
        charToInsert +
        cleanedNumericValue.slice(reversePosition);

      // limpiar parte entera solo 1 cero, para poder calcular posicion del cursor
      let newParts = cleanedNumericValue.split(".");
      if (newParts.length > 1) {
        // captura
        //https://stackoverflow.com/questions/60509557/regex-remove-leading-zeros-but-keep-single-zero
        const nuevoEntero = newParts[0].replace(
          /^(?:0+(?=[1-9])|0+(?=0$))/gm,
          "",
        );
        newParts[0] = nuevoEntero;
      }

      //  cleanedNumericValue = parts[0] + "." + parts.slice(1).join("");
    }

    // --- 2. STATE UPDATE (Formatting) ---
    // Generate the new formatted string based on the cleaned input
    const newDisplayValue = formatCurrency(cleanedNumericValue);

    // Update React state *first*. This triggers a re-render and updates the visible value.
    setDisplayValue(newDisplayValue);

    // --- 3. CURSOR POSITION MAINTENANCE (The Crucial Step) ---
    // To maintain the cursor, we must manually set the selection range AFTER the DOM update.

    // A complex rule: If formatting added characters (e.g., commas), the logical position
    // of the cursor should be relative to where the user was typing, not the formatted string's length.

    let newCursorPosition = event.target.selectionStart;

    if (newDisplayValue !== rawInput) {
      // Simple heuristic: If the displayed value is different from the input,
      // try to maintain a stable cursor position relative to the change.
      const currentDOMLength = rawInput.length; // Use the raw length as a baseline approximation

      if (currentDOMLength > 0) {
        // We don't want the cursor to jump past the end of the new, formatted value
        newCursorPosition = Math.min(newCursorPosition, newDisplayValue.length);
      } else {
        newCursorPosition = 0; // Start at beginning if nothing was typed
      }
    }

    // Use a timeout to ensure that React has finished setting the input's value
    // in the DOM before we try to manipulate its selection range.
    setTimeout(() => {
      if (inputRef.current) {
        const inputElement = inputRef.current;
        // SetCursorRange(start, end): We set both start and end to the same position for a clean cursor.
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0); // Using setTimeout ensures the DOM update completes first

    onNewValueHandler?.(cleanedNumericValue);
  }, []);

  return (
    <>
      {/*<div style={{ margin: "20px", fontFamily: "sans-serif" }}>*/}
      <input
        ref={inputRef} // Attach the ref to the input element
        id="currency-input"
        className={className}
        type="text" // Must use type="text" for custom formatting
        value={displayValue} // Use the formatted state value
        onChange={handleChange} // Use the specialized handler
        placeholder={placeholder}
        style={{ padding: "8px", border: "1px solid #ccc", ...style }}
      />
      {/*</div>*/}
    </>
  );
};
